import React from 'react';

/**
 * MacroRing — kcal donut split into 3 colored arcs by macro contribution.
 *
 * Outer ring divides 360° into 3 arcs proportional to kcal contributed by
 * each macro today (P*4 + C*4 + F*9 = total kcal). Center shows the kcal
 * total (and target if available). Below the ring, three macro bars use
 * neon-tinted fills so the page never feels grey.
 *
 * Props:
 *   totals  : { kcal, protein_g, carb_g, fat_g }
 *   targets : { daily_kcal_target, protein_target_g, carb_target_g, fat_target_g }
 *   compact : if true, render a smaller variant (used on Dashboard tile + chat card)
 */
const COLOR_P = '#f97316';   // orange — protein
const COLOR_C = '#3b82f6';   // blue   — carbs
const COLOR_F = '#a855f7';   // purple — fat

const MacroRing = ({ totals = {}, targets = null, compact = false }) => {
  const kcal = Math.max(0, Math.round(totals.kcal || 0));
  const kcalTarget = targets?.daily_kcal_target || null;
  const remaining = kcalTarget !== null ? Math.max(0, kcalTarget - kcal) : null;

  const p = Math.max(0, totals.protein_g || 0);
  const cb = Math.max(0, totals.carb_g || 0);
  const f = Math.max(0, totals.fat_g || 0);
  const kcalP = p * 4;
  const kcalC = cb * 4;
  const kcalF = f * 9;
  const sumKcal = kcalP + kcalC + kcalF || 1;

  // What fraction of the ring is "filled" so far (vs target if known else 1.0)
  const ringPct = kcalTarget ? Math.min(1, kcal / kcalTarget) : (sumKcal > 0 ? 1 : 0);

  const size = compact ? 96 : 144;
  const stroke = compact ? 8 : 12;
  const cx = size / 2;
  const cy = size / 2;
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;

  // Each macro arc length on the ring
  const lenP = (kcalP / sumKcal) * circ * ringPct;
  const lenC = (kcalC / sumKcal) * circ * ringPct;
  const lenF = (kcalF / sumKcal) * circ * ringPct;

  // SVG strokeDasharray pattern: arc length, then full gap of (circ - arc)
  const arc = (len, offset) => ({
    strokeDasharray: `${len} ${circ}`,
    strokeDashoffset: -offset,
  });

  return (
    <div className={`flex items-center ${compact ? 'gap-3' : 'gap-6'} flex-wrap`} data-testid="macro-ring">
      <div className="relative flex-shrink-0" style={{ width: size, height: size }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90">
          {/* Track */}
          <circle cx={cx} cy={cy} r={r} fill="none" stroke="#1f1f23" strokeWidth={stroke} />

          {/* Macro arcs (only when we have macros logged) */}
          {sumKcal > 1 && (
            <>
              <circle cx={cx} cy={cy} r={r} fill="none" stroke={COLOR_P} strokeWidth={stroke}
                strokeLinecap="butt" {...arc(lenP, 0)}
                style={{ transition: 'all 700ms ease' }} />
              <circle cx={cx} cy={cy} r={r} fill="none" stroke={COLOR_C} strokeWidth={stroke}
                strokeLinecap="butt" {...arc(lenC, lenP)}
                style={{ transition: 'all 700ms ease' }} />
              <circle cx={cx} cy={cy} r={r} fill="none" stroke={COLOR_F} strokeWidth={stroke}
                strokeLinecap="butt" {...arc(lenF, lenP + lenC)}
                style={{ transition: 'all 700ms ease' }} />
            </>
          )}

          {/* Subtle outer accent dot at top to anchor the eye */}
          <circle cx={cx} cy={stroke / 2} r={stroke / 2.4} fill="#27272a" />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className={`${compact ? 'text-xl' : 'text-3xl'} font-bold tracking-tight`}>{kcal}</span>
          <span className="text-[10px] text-zinc-500 uppercase tracking-wider">
            {kcalTarget ? `/ ${kcalTarget}` : 'kcal'}
          </span>
          {!compact && remaining !== null && (
            <span className="text-xs text-zinc-400 mt-0.5">{remaining} left</span>
          )}
        </div>
      </div>

      {/* Macro bars */}
      <div className={`flex-1 min-w-[160px] ${compact ? 'space-y-1.5' : 'space-y-3'}`}>
        {[
          { key: 'protein_g', label: 'Protein', target: targets?.protein_target_g, color: COLOR_P },
          { key: 'carb_g',    label: 'Carbs',   target: targets?.carb_target_g,    color: COLOR_C },
          { key: 'fat_g',     label: 'Fat',     target: targets?.fat_target_g,     color: COLOR_F },
        ].map(({ key, label, target, color }) => {
          const v = Math.round(totals[key] || 0);
          const pct = target ? Math.min(100, Math.round((v / target) * 100)) : (v > 0 ? 100 : 0);
          return (
            <div key={key} data-testid={`macro-${key}`}>
              <div className={`flex justify-between ${compact ? 'text-[11px]' : 'text-xs'} mb-1`}>
                <span className="text-zinc-200 font-medium" style={{ color }}>{label}</span>
                <span className="text-zinc-400">{v}g{target ? ` / ${target}g` : ''}</span>
              </div>
              <div className={`${compact ? 'h-1.5' : 'h-2'} bg-zinc-800 rounded overflow-hidden`}>
                <div
                  className="h-full rounded transition-all duration-700"
                  style={{ width: `${pct}%`, background: color, boxShadow: `0 0 6px ${color}66` }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MacroRing;
